import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import Icon from '../Icon';

export default function EventSourcingInfographic() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const diaryEntries = [
    { time: '2:00pm', event: 'Auction Started', symbol: 'S', color: '#10b981' },
    { time: '2:01pm', event: 'Alice bid £50', symbol: 'B', color: '#6366f1' },
    { time: '2:02pm', event: 'Bob bid £55', symbol: 'B', color: '#ec4899' },
    { time: '2:05pm', event: 'Alice bid £60', symbol: 'B', color: '#6366f1' },
  ];

  return (
    <div ref={ref} className="relative w-full">
      {/* Friendly explanation */}
      <div className="mb-8 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Icon name="history" className="text-2xl text-amber-600" /> What's Event Sourcing?
        </h3>
        <p className="text-gray-600 leading-relaxed">
          Instead of just remembering <em>where we are</em>, we remember <strong>everything that happened</strong>.
          It's like keeping a diary instead of just a note!
        </p>
        <ul className="mt-3 space-y-2 text-gray-600">
          <li className="flex items-start gap-2">
            <Icon name="check" size="small" className="text-green-500 mt-1" />
            <span>Every bid, every change gets <strong>written down forever</strong></span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="check" size="small" className="text-green-500 mt-1" />
            <span>We can <strong>replay history</strong> to recreate any moment</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="check" size="small" className="text-green-500 mt-1" />
            <span>Nothing ever gets deleted or modified - <strong>append only</strong></span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="check" size="small" className="text-green-500 mt-1" />
            <span>Perfect for <strong>disputes</strong> - "who bid first?" is always answerable</span>
          </li>
        </ul>
      </div>

      {/* Animated Infographic */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <svg viewBox="0 0 500 320" className="w-full h-auto">
          {/* Background */}
          <rect x="0" y="0" width="500" height="320" fill="#fafafa" rx="12" />
          
          {/* Title */}
          <text x="250" y="30" textAnchor="middle" fontSize="14" fill="#374151" fontWeight="600">
            Event Sourcing: The Auction Diary
          </text>

          {/* Diary/Journal book */}
          <motion.g
            initial={{ scale: 0, rotate: -10 }}
            animate={isInView ? { scale: 1, rotate: 0 } : {}}
            transition={{ duration: 0.6, type: 'spring' }}
          >
            {/* Book spine */}
            <rect x="30" y="50" width="20" height="200" rx="3" fill="#92400e" />
            {/* Book cover */}
            <rect x="50" y="50" width="180" height="200" rx="3" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" />
            {/* Book title */}
            <text x="140" y="75" textAnchor="middle" fontSize="11" fill="#92400e" fontWeight="bold">Auction Diary</text>
            <line x1="60" y1="85" x2="220" y2="85" stroke="#f59e0b" strokeWidth="1" />
          </motion.g>

          {/* Diary entries */}
          {diaryEntries.map((entry, i) => (
            <motion.g
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.5 + i * 0.3 }}
            >
              <text x="70" y={110 + i * 35} fontSize="9" fill="#6b7280">{entry.time}</text>
              <circle cx="110" cy={106 + i * 35} r="10" fill={entry.color} opacity="0.8" />
              <text x="110" y={111 + i * 35} textAnchor="middle" fontSize="10" fill="white" fontWeight="bold">{entry.symbol}</text>
              <text x="130" y={111 + i * 35} fontSize="11" fill="#374151">{entry.event}</text>
            </motion.g>
          ))}

          {/* "Never deleted" stamp */}
          <motion.g
            initial={{ scale: 0, rotate: 20 }}
            animate={isInView ? { scale: 1, rotate: -5 } : {}}
            transition={{ duration: 0.4, delay: 2 }}
          >
            <rect x="150" y="195" width="70" height="30" rx="4" fill="none" stroke="#dc2626" strokeWidth="2" />
            <text x="185" y="215" textAnchor="middle" fontSize="9" fill="#dc2626" fontWeight="bold">PERMANENT</text>
          </motion.g>

          {/* Arrow showing replay */}
          <motion.path
            d="M 240 150 C 280 150, 290 100, 330 100"
            stroke="#8b5cf6"
            strokeWidth="3"
            fill="none"
            strokeDasharray="6 3"
            initial={{ pathLength: 0 }}
            animate={isInView ? { pathLength: 1 } : {}}
            transition={{ duration: 0.8, delay: 2.5 }}
          />
          <motion.text
            x="285"
            y="90"
            fontSize="10"
            fill="#7c3aed"
            fontWeight="500"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 3 }}
          >
            Replay events →
          </motion.text>

          {/* Current state box */}
          <motion.g
            initial={{ scale: 0 }}
            animate={isInView ? { scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 3.2, type: 'spring' }}
          >
            <rect x="340" y="70" width="140" height="100" rx="12" fill="#f0fdf4" stroke="#22c55e" strokeWidth="2" />
            <text x="410" y="95" textAnchor="middle" fontSize="11" fill="#166534" fontWeight="600">Current State</text>
            <line x1="355" y1="105" x2="465" y2="105" stroke="#86efac" strokeWidth="1" />
            
            <text x="360" y="125" fontSize="10" fill="#374151">Current Bid:</text>
            <text x="450" y="125" textAnchor="end" fontSize="10" fill="#166534" fontWeight="bold">£60</text>
            
            <text x="360" y="145" fontSize="10" fill="#374151">Leader:</text>
            <text x="450" y="145" textAnchor="end" fontSize="10" fill="#6366f1" fontWeight="bold">Alice</text>
            
            <text x="360" y="165" fontSize="10" fill="#374151">Total Bids:</text>
            <text x="450" y="165" textAnchor="end" fontSize="10" fill="#166534" fontWeight="bold">3</text>
          </motion.g>

          {/* Time travel illustration */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 3.5 }}
          >
            <rect x="300" y="200" width="180" height="90" rx="8" fill="#faf5ff" stroke="#a855f7" strokeWidth="1" strokeDasharray="4 2" />
            <text x="390" y="225" textAnchor="middle" fontSize="11" fill="#7c3aed" fontWeight="600">Time Travel!</text>
            <text x="390" y="245" textAnchor="middle" fontSize="9" fill="#6b7280">Jump to any moment:</text>
            <text x="390" y="265" textAnchor="middle" fontSize="10" fill="#374151">"What was the state at 2:01pm?"</text>
            <text x="390" y="280" textAnchor="middle" fontSize="10" fill="#22c55e" fontWeight="500">→ Replay events 1-2 = Done!</text>
          </motion.g>

          {/* Key point */}
          <motion.g
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 4 }}
          >
            <rect x="30" y="270" width="220" height="35" rx="6" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1" />
            <text x="140" y="292" textAnchor="middle" fontSize="10" fill="#1d4ed8" fontWeight="500">
              Never lose history. Perfect audit trail!
            </text>
          </motion.g>
        </svg>
      </div>

      {/* Real-world analogy */}
      <div className="bg-amber-50 rounded-xl p-5 border border-amber-100 mb-6">
        <div className="flex items-start gap-3">
          <Icon name="account_balance" className="text-2xl text-amber-600" />
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Think of a Bank Statement</h4>
            <p className="text-sm text-gray-600">
              Your bank doesn't just show your balance. It shows <strong>every transaction</strong>.
              If there's a mistake, you can trace it back. You can see exactly how you got to your 
              current balance. That's event sourcing for money!
            </p>
          </div>
        </div>
      </div>

      {/* Advanced Concepts */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-100">
        <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Icon name="psychology" className="text-purple-600" />
          Going Deeper
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Snapshots */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 4.2 }}
            className="bg-white rounded-xl p-4 border border-gray-200"
          >
            <div className="flex items-center gap-2 mb-2">
              <Icon name="photo_camera" className="text-blue-500" />
              <h5 className="font-semibold text-gray-900">Snapshots</h5>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              After 1,000 events, replaying from scratch would be slow. 
              So we take <strong>periodic snapshots</strong>, a photo of the current state.
            </p>
            <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
              <code>Events 1-999</code> + <code>Snapshot@1000</code> → Fast recovery!
            </div>
          </motion.div>

          {/* Replay */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 4.4 }}
            className="bg-white rounded-xl p-4 border border-gray-200"
          >
            <div className="flex items-center gap-2 mb-2">
              <Icon name="replay" className="text-green-500" />
              <h5 className="font-semibold text-gray-900">Replay</h5>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Lost your state? No problem. Load the latest snapshot, 
              then replay events since then. <strong>State is reconstructed exactly.</strong>
            </p>
            <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
              Snapshot@1000 + Events 1001-1050 → Current state
            </div>
          </motion.div>

          {/* Crash Recovery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 4.6 }}
            className="bg-white rounded-xl p-4 border border-gray-200"
          >
            <div className="flex items-center gap-2 mb-2">
              <Icon name="restart_alt" className="text-orange-500" />
              <h5 className="font-semibold text-gray-900">Crash Recovery</h5>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Durable Object crashes? Cloudflare restarts it. 
              The actor wakes up, replays its events, and <strong>continues exactly where it left off</strong>.
            </p>
            <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
              No data loss. No inconsistent state. Just recovery.
            </div>
          </motion.div>

          {/* Idempotency */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 4.8 }}
            className="bg-white rounded-xl p-4 border border-gray-200"
          >
            <div className="flex items-center gap-2 mb-2">
              <Icon name="fingerprint" className="text-purple-500" />
              <h5 className="font-semibold text-gray-900">Idempotency</h5>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Each event has a unique ID. Replay the same event twice? 
              It's detected and ignored. <strong>No duplicate bids, ever.</strong>
            </p>
            <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
              Event ID: <code>bid_abc123</code> → Process once, skip duplicates
            </div>
          </motion.div>
        </div>

        {/* Ordering Guarantee */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 5 }}
          className="mt-4 bg-green-50 rounded-xl p-4 border border-green-200"
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="format_list_numbered" className="text-green-600" />
            <h5 className="font-semibold text-green-800">Ordering Guarantee</h5>
          </div>
          <p className="text-sm text-green-700">
            Events are appended in the <strong>exact order they were processed</strong>. 
            Combined with the actor's single-threaded mailbox, this means: 
            <strong> "Who bid first?" is always answerable, deterministically.</strong>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
